import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

// Stripe price configuration
const PRICING_PLANS = {
  monthly: {
    priceId: 'price_1Sego7BCcNMrUAO5q5yCfkJo',
    price: 69,
    interval: 'month'
  },
  annual: {
    priceId: 'price_1SegqHBCcNMrUAO5lIjhiLQP',
    price: 708,
    interval: 'year'
  }
};

const CheckoutButton: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');

  const handleCheckout = async (priceId: string) => {
    setLoading(true);
    setError(null);

    if (!user) {
      setError('You must be logged in to subscribe.');
      setLoading(false);
      return;
    }

    try {
      const { data, error: functionError } = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          userId: user.id,
          priceId: priceId
        },
      });

      if (functionError) {
        throw functionError;
      }

      const { sessionId } = data;
      const stripe = await stripePromise;
      if (stripe) {
        stripe.redirectToCheckout({ sessionId });
      }
    } catch (error: any) {
      setError('Payment failed. Please check your card details and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Plan Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Monthly Plan */}
        <div 
          className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
            selectedPlan === 'monthly' 
              ? 'border-accent-blue bg-accent-blue-wash' 
              : 'border-surface-border hover:border-surface-border'
          }`}
          onClick={() => setSelectedPlan('monthly')}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-medium text-text-primary">Monthly</h3>
              <p className="text-text-muted">Perfect for getting started</p>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-bold text-text-primary">${PRICING_PLANS.monthly.price}</div>
              <div className="text-sm text-text-muted">per month</div>
            </div>
          </div>
          <div className="mt-3">
            <input
              type="radio"
              name="plan"
              checked={selectedPlan === 'monthly'}
              onChange={() => setSelectedPlan('monthly')}
              className="h-4 w-4 text-accent-blue focus:ring-accent-blue border-surface-border"
            />
          </div>
        </div>

        {/* Annual Plan */}
        <div 
          className={`p-6 border-2 rounded-lg cursor-pointer transition-all relative ${
            selectedPlan === 'annual' 
              ? 'border-accent-blue bg-accent-blue-wash' 
              : 'border-surface-border hover:border-surface-border'
          }`}
          onClick={() => setSelectedPlan('annual')}
        >
          <div className="absolute top-0 right-0 bg-status-success text-white px-2 py-1 text-xs rounded-bl-lg rounded-tr-lg">
            Save 15%
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-medium text-text-primary">Annual</h3>
              <p className="text-text-muted">Best value for committed users</p>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-bold text-text-primary">${PRICING_PLANS.annual.price}</div>
              <div className="text-sm text-text-muted">per year</div>
              <div className="text-xs text-status-success">~$59/month</div>
            </div>
          </div>
          <div className="mt-3">
            <input
              type="radio"
              name="plan"
              checked={selectedPlan === 'annual'}
              onChange={() => setSelectedPlan('annual')}
              className="h-4 w-4 text-accent-blue focus:ring-accent-blue border-surface-border"
            />
          </div>
        </div>
      </div>

      {/* Subscribe Button */}
      <button
        onClick={() => handleCheckout(PRICING_PLANS[selectedPlan].priceId)}
        disabled={loading}
        className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Redirecting to Checkout...
          </>
        ) : (
          `Subscribe ${selectedPlan === 'monthly' ? 'Monthly' : 'Annually'} - $${PRICING_PLANS[selectedPlan].price}`
        )}
      </button>
      
      {error && (
        <div className="p-3 bg-status-danger-light border border-status-danger/20 rounded-md">
          <p className="text-status-danger text-sm">{error}</p>
        </div>
      )}

      {/* Features List */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-text-primary mb-3">What's included:</h4>
        <ul className="space-y-2 text-sm text-text-body">
          <li className="flex items-center">
            <svg className="h-4 w-4 text-status-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Complete EHS Management Platform
          </li>
          <li className="flex items-center">
            <svg className="h-4 w-4 text-status-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Incident Tracking & Reporting
          </li>
          <li className="flex items-center">
            <svg className="h-4 w-4 text-status-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            OSHA Compliance Tools
          </li>
          <li className="flex items-center">
            <svg className="h-4 w-4 text-status-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Training Management
          </li>
          <li className="flex items-center">
            <svg className="h-4 w-4 text-status-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Safety Audits & Inspections
          </li>
          <li className="flex items-center">
            <svg className="h-4 w-4 text-status-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            24/7 Customer Support
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CheckoutButton;

export {};
